import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Box, Button, Checkbox, FormControlLabel, Popover, Stack } from '@mui/material';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import {
  computePercentChange,
  DashboardBlock,
  DashboardGroupHeading,
  DashboardPeriod,
  getPeriodRange,
  PeriodSelect,
  StatTile,
} from '@retail/dashboard/components';
import { useFetchCompetitorPriceSummary } from '@retail/dashboard/data-access';
import { useDashboardTFunction } from '@retail/dashboard/i18n';
import { CompetitorPricesChart, CompetitorSeries } from './CompetitorPricesChart';
import { MarketIcon } from '@shared/custom-icons';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '@retail/products/utils';

interface Props {
  period: DashboardPeriod;
  onPeriodChange: (period: DashboardPeriod) => void;
  periods: readonly DashboardPeriod[];
}

export const CompetitorPricesBlock = ({ period, onPeriodChange, periods }: Props) => {
  const t = useDashboardTFunction();
  const { i18n } = useTranslation();
  const { selectedOrgUnitAssortment } = useSelectedOrgUnit();
  const assortmentId = selectedOrgUnitAssortment.assortmentId;

  const [selected, setSelected] = useState<string[] | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const range = useMemo(() => getPeriodRange(period), [period]);
  const fmt = (d: dayjs.Dayjs) => d.format('YYYY-MM-DD');

  const prices = useFetchCompetitorPriceSummary({
    assortmentId,
    from: fmt(range.previousFrom),
    suspense: false,
    refetchOnWindowFocus: false,
  });

  const boundary = fmt(range.from);
  const currentData = useMemo(
    () => (prices.data ?? []).filter((p) => p.date >= boundary),
    [prices.data, boundary]
  );
  const previousData = useMemo(
    () => (prices.data ?? []).filter((p) => p.date < boundary),
    [prices.data, boundary]
  );

  // The API only returns days with updates, so fill in every day of the period with 0.
  const buckets = useMemo(() => {
    const today = dayjs().endOf('day');
    const last = range.to.isBefore(today) ? range.to : today;
    const days: string[] = [];
    for (let day = range.from; !day.isAfter(last); day = day.add(1, 'day')) days.push(fmt(day));
    return days;
  }, [range]);
  const bucketLabels = useMemo(() => buckets.map((b) => dayjs(b).format('DD.MM')), [buckets]);

  const allNames = useMemo(
    () => Array.from(new Set(currentData.map((p) => p.retailerName))),
    [currentData]
  );
  const selectedNames = selected ?? allNames;

  const toggle = (name: string) =>
    setSelected((prev) => {
      const base = prev ?? allNames;
      return base.includes(name) ? base.filter((x) => x !== name) : [...base, name];
    });

  const series: CompetitorSeries[] = useMemo(
    () =>
      allNames
        .filter((name) => selectedNames.includes(name))
        .map((name) => ({ name, colorIndex: allNames.indexOf(name) })),
    [allNames, selectedNames]
  );

  const rows = useMemo(() => {
    const byBucket = new Map<string, Record<string, number | string>>(
      buckets.map((b) => [
        b,
        { bucket: b, ...Object.fromEntries(selectedNames.map((n) => [n, 0])) },
      ])
    );
    currentData.forEach((p) => {
      const row = byBucket.get(p.date);
      if (row && selectedNames.includes(p.retailerName)) row[p.retailerName] = p.count;
    });
    return Array.from(byBucket.values());
  }, [buckets, currentData, selectedNames]);

  const currentTotal = useMemo(
    () =>
      currentData
        .filter((p) => selectedNames.includes(p.retailerName))
        .reduce((sum, p) => sum + p.count, 0),
    [currentData, selectedNames]
  );
  const previousTotal = useMemo(
    () =>
      previousData
        .filter((p) => selectedNames.includes(p.retailerName))
        .reduce((sum, p) => sum + p.count, 0),
    [previousData, selectedNames]
  );

  const isEmpty = !prices.isLoading && !prices.isError && currentData.length === 0;

  return (
    <Stack gap={1.5} sx={{ height: '100%' }}>
      <DashboardGroupHeading
        title={t('dashboard.updatedCompetitorPrices')}
        icon={<MarketIcon fontSize="small" />}
        action={
          <Stack direction="row" gap={1} alignItems="center">
            <PeriodSelect value={period} onChange={onPeriodChange} periods={periods} />
          </Stack>
        }
      />
      <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex' }}>
        <DashboardBlock
          isLoading={prices.isLoading}
          isError={prices.isError}
          onRetry={prices.refetch}
          isEmpty={isEmpty}
          minHeight={300}
        >
          <Stack gap={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="start" gap={2}>
              <StatTile
                label={t('dashboard.competitorPrices.total')}
                value={formatPrice(currentTotal, i18n.language)}
                change={
                  range.isFuture ? undefined : computePercentChange(currentTotal, previousTotal)
                }
                caption={t('dashboard.period.vsPrevious')}
              />
              <Button
                size="small"
                color="secondary"
                variant="outlined"
                startIcon={<TuneOutlinedIcon fontSize="small" />}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
              >
                {t('dashboard.competitorPrices.selectCompetitors')} ({selectedNames.length}/
                {allNames.length})
              </Button>
              <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Stack sx={{ p: 1.5, maxHeight: 320, overflowY: 'auto' }}>
                  {allNames.map((name) => (
                    <FormControlLabel
                      key={name}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedNames.includes(name)}
                          onChange={() => toggle(name)}
                        />
                      }
                      label={name}
                    />
                  ))}
                </Stack>
              </Popover>
            </Stack>

            <Box>
              <CompetitorPricesChart
                buckets={buckets}
                bucketLabels={bucketLabels}
                series={series}
                rows={rows}
              />
            </Box>
          </Stack>
        </DashboardBlock>
      </Box>
    </Stack>
  );
};
